import {useEffect} from 'react';
import {useIntl} from '@sail/intl';

import {useRetrieveFundingSession} from 'src/fund/api/useRetrieveFundingSession';
import {getStore} from 'src/fund/state/store';
import {UserFacingError} from 'src/utils/error';
import messages from 'src/utils/messages';
import type {RetrieveFundingSessionQueryToken} from 'src/fund/api/useRetrieveFundingSessionQuery';
import type {
  ValidateAllPermissions,
  FunctionParameterToken,
  ValidatePermissions,
} from '@sail/data';

export const useStateInitialize = (
  fundingSessionId: string,
  token: RetrieveFundingSessionQueryToken,
) => {
  const intl = useIntl();
  const {data, loading, error} = useRetrieveFundingSession(
    fundingSessionId,
    token,
  );
  const fundingSession = data?.v2RetrieveFundingSession;
  const store = getStore();

  useEffect(() => {
    if (!fundingSession || fundingSession.id !== fundingSessionId) {
      return;
    }

    store.setValue((state) => {
      state.cryptoWalletFundingOptions =
        fundingSession.fundingOptions.cryptoWallet;
      state.businessName = fundingSession.businessName;
      state.logoUrl = fundingSession.logoUrl;
      state.merchantBranding = fundingSession.merchantBranding;
    });
  }, [fundingSession, fundingSessionId, store]);

  if (error) {
    if (
      !error.networkError &&
      error.graphQLErrors.length > 0 &&
      error.graphQLErrors.every(
        ({extensions}) => extensions?.error?.code === 'not_found',
      )
    ) {
      // Retrieval intentionally uses not_found for both missing and expired sessions.
      throw new UserFacingError(intl.formatMessage(messages.error404));
    }
    throw error;
  }

  if (!loading && (!fundingSession || fundingSession.id !== fundingSessionId)) {
    throw new UserFacingError(intl.formatMessage(messages.error404));
  }

  return {loading};
};

// THIS IS AUTOGENERATED CODE FOR PERMISSION TOKEN, DO NOT EDIT AS YOUR CHANGES WILL BE BLOWN AWAY

// prettier-ignore
export type validate = ValidateAllPermissions<
  ValidatePermissions<RetrieveFundingSessionQueryToken, [
    FunctionParameterToken<typeof useRetrieveFundingSession, 1>
  ]>
>;
